import { Button, LinearProgress } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import {Column, Table, AutoSizer} from 'react-virtualized';
import { store } from '../../stores/Store';
import Ajax from '../../Ajax';
import InlineMenu from '../../misc/InlineMenu';
import { format } from 'date-fns';
import Field from './fields/Field';


interface JobSelectFieldProps {
    accountUid: string;
    value?: string;
    onChange?(string): void;
    warning?: string | false;
    helpText?: string | false;
}


export default function JobSelectField ({accountUid, value = null, onChange = null, warning = false, helpText = false}: JobSelectFieldProps) {

    const [account, setAccount] = React.useState(null)
    const [searchString, setSearchString] = React.useState(null)
    const [hideUnavailableJobs, setHideUnavailableJobs] = React.useState(true)

    React.useEffect(() => {
        Ajax.Node.Get(accountUid).then((response) => {
            setAccount(response.data)
        })
    }, [])

    const rowHeight = 30
    const minWidth = 600
    const jobs = !account ? [] : account?.childrenObjects.filter((co) => {
        if (co.uid === value) return true
        if (!co.typeName.includes("JobNode")) return false
        if (searchString && (
            !co.title?.toLowerCase().includes((searchString.toLowerCase())) &&
            !co.occupationCode?.includes((searchString))
        )) return false
        if (hideUnavailableJobs && co.toDate && new Date(co.toDate) < new Date()) return false
        return true
    }).sort((a, b) => {
        return b.fromDate?.localeCompare(a.fromDate)
    })

    return (
        <div>
            <div style={{display: "flex", gap: "5px", alignItems: "baseline", marginBottom: "10px"}}>
                <div className="form-search">
                    <input 
                        autoFocus 
                        value={searchString}
                        placeholder="Search"
                        spellCheck="false"
                        onChange={(e) => setSearchString(e.target.value)} />
                </div>

                {account ? (
                    <small>{jobs?.length} Jobs</small>
                ) : (
                    <small>Loading Jobs</small>
                )}
            </div>

            <Field.Checkbox 
                label="Hide jobs which aren't available anymore" 
                onChange={() => setHideUnavailableJobs(!hideUnavailableJobs)} 
                value={hideUnavailableJobs} />

            <div className={`rv-table-wrapper rv-table-md`} style={{minHeight: "300px", boxShadow: "0 -15px 24px -29px inset black"}}>
                <AutoSizer>
                    {({height, width}) => (
                        <Table
                            width={width < minWidth ? minWidth : width}
                            height={height}
                            headerHeight={rowHeight}
                            rowHeight={rowHeight}
                            rowCount={jobs.length}
                            rowGetter={({index}) => jobs[index]}
                            onRowClick={({event, index, rowData}) => {
                                if (onChange) {
                                    onChange(rowData.uid)   
                                }
                            }} >
                            
                            <Column
                                width={110}
                                dataKey=""
                                headerRenderer={() => <>Job title</>}
                                cellRenderer={(data) => (
                                    <span style={{fontWeight: value === data.rowData.uid ? 600 : "unset"}}>
                                        {data.rowData.title}
                                    </span>
                                )} />
                                
                            <Column
                                width={80}
                                dataKey=""
                                headerRenderer={() => <>Job code</>}
                                cellRenderer={(data) => (
                                    <span style={{fontWeight: value === data.rowData.uid ? 600 : "unset"}}>
                                        {data.rowData.occupationCode}
                                    </span>
                                )} />

                            <Column
                                width={140}
                                dataKey=""
                                headerRenderer={() => <>Case</>}
                                cellRenderer={(data) => {
                                    if (!data.rowData.relevantCases?.length) {
                                        return <span className="font-grey">Not used</span>
                                    }
                                    if (data.rowData.relevantCases?.length === 1) {
                                        return (
                                            <Link className='link' to={`/app/main/${data.rowData.relevantCases[0].uid}/${data.rowData.uid}`}>
                                                {data.rowData.relevantCases[0].name}    
                                            </Link>
                                        )
                                    }
                                    var phrase = `Used in ${data.rowData.relevantCases?.length} Cases`
                                    return (
                                        <div style={{display: "flex", gap: "5px"}}>
                                            <InlineMenu opener={<span className="link">{phrase}</span>}>
                                                <div style={{padding: "10px", display: "grid", gap: "5px", minWidth: "100px"}}>
                                                    {data.rowData.relevantCases?.map((nodeRef, i) => (
                                                        <span>
                                                            <Link className='link' to={`/app/main/${nodeRef.uid}/${data.rowData.uid}`}>
                                                                {nodeRef.name}    
                                                            </Link>
                                                        </span>
                                                    ))}
                                                </div>
                                            </InlineMenu>
                                        </div>
                                    )
                                }} />

                            <Column
                                width={110}
                                dataKey=""
                                headerRenderer={() => <>Created at</>}
                                cellRenderer={(data) => (
                                    <>{data.rowData.fromDate ? format(new Date(data.rowData.fromDate), "yyyy-MM-dd") : ""}</>
                                )} />
                            
                            <Column
                                width={110}
                                dataKey=""
                                headerRenderer={() => <>Status</>}
                                cellRenderer={(data) => {
                                    const active = data.rowData.toDate && new Date(data.rowData.toDate) > new Date()
                                    return (
                                        <div className="font-grey">
                                            {active ? "Active" : "Deactived"}
                                        </div>
                                    )
                                }} />
                        </Table>
                    )}
                </AutoSizer>

                {!account ? (
                    <LinearProgress />
                ) : null} 

                {helpText !== false ? (
                    <div className="help-text">{helpText}</div>
                ) : null} 
                
                {warning !== false ? (
                    <div className="warning">{warning}</div>
                ) : null} 
            </div>
        </div>
    )
}